import { QueryClient, QueryFunction, QueryCache } from "@tanstack/react-query";

// Guard against multiple simultaneous 401-triggered redirects
let _redirectingOn401 = false;
// Same guard for the brand-disabled (403 FRANCHISE_INACTIVE) redirect
let _redirectingOnBrandDisabled = false;

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    let message = `${res.status}: ${text}`;
    let errorCode: string | undefined;
    try {
      const json = JSON.parse(text);
      // Most API routes return { error: "..." }; some return { message: "..." }
      const detail = json.message || json.error;
      if (detail) {
        message = `${res.status}: ${detail}`;
      }
      if (typeof json.code === 'string') errorCode = json.code;
    } catch {
      // Response was not JSON; keep the raw text
    }
    const err = new Error(message);
    if (errorCode) (err as any).code = errorCode;
    throw err;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const authHeaders = getAuthHeaders();
  const res = await fetch(url, {
    method,
    headers: {
      ...(data ? { "Content-Type": "application/json" } : {}),
      ...authHeaders,
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
// Helper function to get auth headers
export const getAuthHeaders = (): Record<string, string> => {
  // For session-based authentication, we rely on cookies
  // No need to send user-id headers as the session handles authentication
  return {};
};

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
      headers: getAuthHeaders(),
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: Error) => {
      // The user's brand was disabled (franchises.is_active = false). Every
      // authenticated query will 403 from here on, so land on a page that
      // explains it instead of a tree full of thrown errors.
      //
      // The pathname check is load-bearing: this redirect is a full navigation,
      // so the in-memory guard above resets on arrival. Without it, a query that
      // still fails on the destination page redirects to the same URL again and
      // the browser reload-loops.
      if (
        !_redirectingOnBrandDisabled &&
        (error as any).code === 'FRANCHISE_INACTIVE' &&
        window.location.pathname !== '/no-franchise'
      ) {
        _redirectingOnBrandDisabled = true;
        window.location.href = '/no-franchise?reason=brand_disabled';
        return;
      }

      // When any authenticated query gets a 401 the backend session has expired.
      // Redirect to root so Firebase auth reinitialises cleanly instead of leaving
      // a partially-mounted tree with thrown errors that cause a blank screen.
      if (!_redirectingOn401 && error.message?.startsWith('401:')) {
        _redirectingOn401 = true;
        window.location.href = '/';
      }
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false, // Disable auto-refresh globally - server handles caching
      refetchOnWindowFocus: false,
      staleTime: 60 * 60 * 1000, // 1 hour default stale time to prevent unnecessary requests
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
